import {
  WorldPoint,
  BuildingBox,
  SurveillanceCamera,
  InspectorNPC,
  TeaStallEntity,
} from '../domain/types.ts';
import { GAME_CONFIG } from '../config/gameConfig.ts';

/**
 * Kiểm tra giao điểm của 2 đoạn thẳng (p1-p2 và p3-p4)
 */
function lineIntersectsLine(
  p1: WorldPoint,
  p2: WorldPoint,
  p3: WorldPoint,
  p4: WorldPoint
): boolean {
  const det = (p2.x - p1.x) * (p4.y - p3.y) - (p2.y - p1.y) * (p4.x - p3.x);
  if (Math.abs(det) < 0.0001) return false;

  const lambda =
    ((p4.y - p3.y) * (p4.x - p1.x) + (p3.x - p4.x) * (p4.y - p1.y)) / det;
  const gamma =
    ((p1.y - p2.y) * (p4.x - p1.x) + (p2.x - p1.x) * (p4.y - p1.y)) / det;

  return 0 <= lambda && lambda <= 1 && 0 <= gamma && gamma <= 1;
}

/**
 * Kiểm tra xem tia nhìn từ start đến target có bị tường nhà cản không
 */
export function hasObstacleBetween(
  start: WorldPoint,
  target: WorldPoint,
  buildings: BuildingBox[]
): boolean {
  for (const b of buildings) {
    const minX = b.x;
    const maxX = b.x + b.width;
    const minY = b.y;
    const maxY = b.y + b.height;

    // 4 cạnh của hình chữ nhật tòa nhà
    const p1 = { x: minX, y: minY };
    const p2 = { x: maxX, y: minY };
    const p3 = { x: maxX, y: maxY };
    const p4 = { x: minX, y: maxY };

    if (
      lineIntersectsLine(start, target, p1, p2) ||
      lineIntersectsLine(start, target, p2, p3) ||
      lineIntersectsLine(start, target, p3, p4) ||
      lineIntersectsLine(start, target, p4, p1)
    ) {
      return true;
    }
  }
  return false;
}

export interface CaughtCheckResult {
  isCaught: boolean;
  watcherType?: 'camera' | 'inspector' | 'teastall' | 'hidden_cam';
  watcherName?: string;
  disgraceMultiplier: number;
  reason?: string;
}

/**
 * Thuật toán kiểm tra tổng hợp: Bãi phân hoặc Người chơi có bị bắt quả tang không?
 */
export function checkSurveillance(
  targetPos: WorldPoint,
  cameras: SurveillanceCamera[],
  inspectors: InspectorNPC[],
  teaStalls: TeaStallEntity[],
  buildings: BuildingBox[]
): CaughtCheckResult {
  // 1. Quán Trà Đá / Bàn Cờ Tướng: Radar 360 độ chạy bằng cơm (Ưu tiên hàng đầu!)
  for (const stall of teaStalls) {
    const dist = Math.hypot(targetPos.x - stall.x, targetPos.y - stall.y);
    if (dist <= stall.radius) {
      if (!hasObstacleBetween({ x: stall.x, y: stall.y }, targetPos, buildings)) {
        return {
          isCaught: true,
          watcherType: 'teastall',
          watcherName: stall.name,
          disgraceMultiplier: GAME_CONFIG.gameTheory.disgraceMultiplier.mainRoad,
          reason: `Bị mấy cô quán nước và bác xe ôm đầu ngõ nhìn thấy 100%!`,
        };
      }
    }
  }

  // 2. Camera An Ninh 4K Cố Định
  for (const cam of cameras) {
    const dx = targetPos.x - cam.x;
    const dy = targetPos.y - cam.y;
    const dist = Math.hypot(dx, dy);

    if (dist <= cam.range) {
      const angleToTarget = Math.atan2(dy, dx);
      let diff = Math.abs(angleToTarget - cam.facingAngle);
      if (diff > Math.PI) diff = Math.PI * 2 - diff;

      if (diff <= cam.fov / 2) {
        if (!hasObstacleBetween({ x: cam.x, y: cam.y }, targetPos, buildings)) {
          return {
            isCaught: true,
            watcherType: 'camera',
            watcherName: cam.buildingName,
            disgraceMultiplier: GAME_CONFIG.gameTheory.disgraceMultiplier.mainRoad,
            reason: `Camera 4K tại ${cam.buildingName} zoom thẳng vào hiện trường!`,
          };
        }
      }
    }
  }

  // 3. NPC Bác Tổ Trưởng Tuần Tra (Nón quét 90 độ)
  for (const npc of inspectors) {
    const dx = targetPos.x - npc.x;
    const dy = targetPos.y - npc.y;
    const dist = Math.hypot(dx, dy);

    if (dist <= npc.range) {
      const angleToTarget = Math.atan2(dy, dx);
      let diff = Math.abs(angleToTarget - npc.facingAngle);
      if (diff > Math.PI) diff = Math.PI * 2 - diff;

      if (diff <= npc.fov / 2) {
        if (!hasObstacleBetween({ x: npc.x, y: npc.y }, targetPos, buildings)) {
          return {
            isCaught: true,
            watcherType: 'inspector',
            watcherName: npc.name,
            disgraceMultiplier: GAME_CONFIG.gameTheory.disgraceMultiplier.mainRoad,
            reason: `${npc.name} bắt tại trận khi đang quét sân!`,
          };
        }
      }
    }
  }

  // 4. Cơ chế Giám sát ẩn (Hidden Surveillance: 5% nguy cơ bị quay lén từ tầng 2 khi ở góc tối)
  const isDarkAlley = targetPos.y < -20 || targetPos.y > 25;
  if (isDarkAlley && Math.random() < GAME_CONFIG.gameTheory.hiddenCamProbability) {
    return {
      isCaught: true,
      watcherType: 'hidden_cam',
      watcherName: 'Bà Hàng Xóm Tầng 2 (Hé rèm quay lén)',
      disgraceMultiplier: GAME_CONFIG.gameTheory.disgraceMultiplier.darkAlley,
      reason: `Tưởng góc tối không ai thấy, ai ngờ bà hàng xóm tầng 2 hé rèm quay nét từng cọng tóc!`,
    };
  }

  return {
    isCaught: false,
    disgraceMultiplier: 1.0,
  };
}
